import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';

// INTERFACES
import {
  IOrderCreateFromItems,
  OrderSalesInterface,
} from '../../../../order/interfaces';
import { ISalesOrderStatus } from '../../../../sales-order/interfaces/sales-order.interface';

// SERVICES
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

import { EOrderType } from './../../../interfaces/order.interface';

@Injectable({
  providedIn: 'root',
})
export class OrderApiService {
  private readonly _orders = new BehaviorSubject<OrderSalesInterface[]>(
    <OrderSalesInterface[]>[],
  );
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);

  orders$ = this._orders.asObservable();
  loading$ = this._loading.asObservable();
  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  // DEPRECATED
  getOrders(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/orders/sales`, {
        params,
      })
      .subscribe({
        next: (response :any) => {
          this._orders.next(response?.data);
          this._loading.next(false);
        },
        error: (error :any) => throwError(() => new Error(error)),
      });
  }

  get(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV2}/orders/sales`, {
      params,
    });
  }

  getGroups(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(`${this.environment.urlV2}/orders/sales/groups`, {
      params,
    });
  }

  getSupplierPurchases(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/orders/supplier-purchases`,
      { params },
    );
  }

  getOrderStatuses() :Observable<ISalesOrderStatus[]> {
    return this.apiService.get(
      `${this.environment.urlV2}/orders/sales/per-order-status-count`,
    );
  }

  create(payload :any) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/supplier-purchases`,
      body: { ...payload },
    });
  }

  shipOrderToShipstation(
    orderId :number,
    data ?:{
      includedOrderItemIds ?:number[];
    },
  ) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV1}/sales/shipstation_sales/${orderId}/shipping`,
      body: { ...data },
    });
  }

  getOrderUpdates(orderId :number) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV1}/sales/shipstation_sales/${orderId}/shipping`,
    );
  }

  updateBackgroundColor(data :{
    id :number;
    backgroundColor :string;
  }) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/${data.id}/cell-formats`,
      body: { ...data },
    });
  }

  updateCustomField(
    orderId :number,
    data :{
      customFieldId :number;
      value :string;
    },
  ) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/${orderId}/custom-field`,
      body: { ...data },
    });
  }

  updateRowColumn(
    orderId :number,
    data :{ [column :string] :string },
  ) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/orders/${orderId}/update-row`,
      data,
    );
  }

  getById(
    salesOrderId :number,
    params ?:{ [param :string] :string | string[] },
  ) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/orders/sales/${salesOrderId}`,
      {
        params,
      },
    );
  }

  createSalesOrderInvoice(shipstationOrderId :number) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/orders/${shipstationOrderId}/invoice`,
    );
  }

  sendEdiTransactionDocument(
    salesOrderId :number,
    ediTransactionNumber :string,
  ) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/orders/${salesOrderId}/edi/${ediTransactionNumber}`,
    );
  }

  sendPoToContacts(data :any) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/send-to-contacts`,
      body: { ...data },
    });
  }

  searchReceivableOrders(params :{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/orders/supplier-receiving-reports/receivables`,
      {
        params,
      },
    );
  }

  getReceivablesByType(params) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/orders/supplier-receiving-reports/receivable-by-type`,
      {
        params,
      },
    );
  }

  createPartialInvoice(
    salesOrderId :number,
    lineItemsOverride :{
      shipstationOrderLineItemId :number;
      invoicedQuantity :number;
    }[],
  ) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/sales/${salesOrderId}/partial-invoice`,
      body: { lineItemsOverride: lineItemsOverride },
    });
  }

  getLineItemRawProducts(data :{
    lineItemId :number;
    orderType :EOrderType;
  }) :Observable<any> {
    const { lineItemId, orderType } = data;

    return this.apiService.get(
      `${this.environment.urlV2}/orders/shared/${orderType}/items/${lineItemId}`,
    );
  }

  createFromItems(data :IOrderCreateFromItems) :Observable<any> {
    return this.apiService.post({
      path: `${this.environment.urlV2}/orders/shared/create-from-items`,
      body: { ...data },
    });
  }
}
